import { useSelector } from 'react-redux';
import { KoddiTheme } from 'koddi-components/ThemeProvider';
// eslint-disable-next-line import/no-cycle
import { useMemberGroupId } from 'redux-core/app/context/hooks';
import { KoddiThemeResource, ThemeError } from './types';
import {
    selectAppTheme,
    selectAppThemeResource,
    selectAppThemeList,
    selectThemeStatus,
    selectThemeError,
    selectThemeInitialized,
    selectThemeRegistration,
    selectThemeSSOIds,
} from './selectors';

export function useAppTheme(): KoddiTheme {
    const theme = useSelector(selectAppTheme);
    if (!theme) {
        throw new Error('Theme not initialized');
    }
    return theme;
}

export function useKoddiThemeResource(): KoddiThemeResource {
    const themeResource = useSelector(selectAppThemeResource);
    if (!themeResource) {
        throw new Error('Theme resource not initialized');
    }
    return themeResource;
}

export function useAppThemeList(): KoddiThemeResource[] {
    return useSelector(selectAppThemeList) ?? [];
}

export function useKoddiThemeResourceByMemberGroupId(
    memberGroupId: number
): KoddiThemeResource | undefined {
    const themes = useSelector(selectAppThemeList);
    return themes?.find(
        (theme) => theme.member_group_id === Number(memberGroupId)
    );
}

export function useMemberGroupTheme(
    memberGroupId: string
): KoddiThemeResource | undefined {
    const themes = useSelector(selectAppThemeList);
    return themes?.find(
        (theme) => theme.member_group_id === Number(memberGroupId)
    );
}

export function useThemeContextChanged(): boolean {
    const memberGroupId = useMemberGroupId();
    const theme = useKoddiThemeResource();
    return memberGroupId !== theme?.member_group_id;
}

export function useThemeStatus(): string {
    return useSelector(selectThemeStatus);
}

export function useThemeError(): ThemeError | null {
    return useSelector(selectThemeError);
}

export function useThemeInitialized(): boolean {
    return useSelector(selectThemeInitialized);
}

export function useThemeHasError(): boolean {
    const status = useThemeStatus();
    const error = useThemeError();
    const memberGroupId = useMemberGroupId();
    return status === 'error' && memberGroupId === error?.memberGroupId;
}

export function useRegistrationEnabled(): boolean {
    return useSelector(selectThemeRegistration);
}

export function useSSOIds(): Record<string, string | undefined> {
    return useSelector(selectThemeSSOIds);
}
